import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'

import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import {
  accountKindLabel,
  addExperimentInstructor,
  getExperimentInstructors,
  onlyInstructorAccounts,
  removeExperimentInstructor,
} from '../../../service/experimentInstructorsService'
import { showToast } from '../../commonUnit/Toast'
import {
  card,
  cardHeader,
  cardSubtitle,
  cardTitle,
  dashboardPage,
  toolbarButton,
} from '../../instructorDashboard/components/dashboardStyles'
import { aiChatEnabled } from '../sessionSettings'
import { ASSIGNMENT_MODES, assignmentLabel, offloadingLabel } from './experimentConfig'

const cellSx = { borderBottom: '1px solid #f3f4f6', fontSize: '14px' }
const headSx = { ...cellSx, color: '#6b7280', fontWeight: 600 }
// The app's global table rules (codeIssuePanel.scss: thead, th, td) draw heavy outer and
// column borders; the Monitor's lists use light row lines only.
const tableSx = {
  border: 'none',
  '& thead': { border: 'none', textAlign: 'left' },
  '& tr': { border: 'none' },
  '& th, & td': { borderLeft: 'none', borderRight: 'none', borderTop: 'none' },
}
const mutedSx = { color: '#9ca3af' }

// The email field: the Monitor's search box, at the height of its toolbar buttons.
const emailInputStyle = {
  flex: 1,
  minWidth: 0,
  maxWidth: '360px',
  padding: '6px 12px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  background: 'white',
  fontSize: '13px',
  color: '#333',
  outline: 'none',
}

const EMAIL_INPUT_ID = 'experiment-instructor-email'

const accountKey = (entry) => `${entry.kind}:${entry.id}`
const sameAccount = (a, b) => a.kind === b.kind && Number(a.id) === Number(b.id)
const nameOf = (entry) => String(entry?.name || '').trim()
const userName = (user) => [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
const capitalized = (text) => text.charAt(0).toUpperCase() + text.slice(1)

// Instructors who can run this experiment besides its creator (backend:
// experimentService.addInstructors): for example the course's instructor, starting the exercise
// in class while the research account keeps access. They can Start, Pause, Resume, Reset and
// End it, but its setup stays the creator's. Only the creator changes the list (canChange);
// anyone else who can open this tab sees it read-only.
const InstructorsCard = ({ experimentId }) => {
  const { currentUser } = useContext(AuthContext) || {}
  const [list, setList] = useState(null) // { creatorId, instructors, canChange }
  const [loadFailure, setLoadFailure] = useState(null) // { message, status }
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(null) // 'add', or the accountKey being removed
  const [actionError, setActionError] = useState('')
  // After an Add by email that matched only an instructor account (see add).
  const [addWarning, setAddWarning] = useState('')
  // Only the newest answer is shown: a load still in flight from before a change must not
  // bring the old list back.
  const latest = useRef(0)

  const load = useCallback(async () => {
    const request = ++latest.current
    const result = await getExperimentInstructors(experimentId)
    if (request !== latest.current) return
    if (result.ok) {
      setList(result)
      setLoadFailure(null)
    } else {
      setLoadFailure({ message: result.message, status: result.status })
    }
  }, [experimentId])

  useEffect(() => {
    load()
  }, [load])

  // Shows the list a change answered with, then re-reads it (also after a failed change: a
  // request that timed out may still have gone through).
  const settle = (result) => {
    if (result.ok) {
      latest.current += 1
      setList((current) => (current ? { ...current, instructors: result.instructors } : current))
    } else {
      setActionError(result.message)
    }
    load()
  }

  const add = async (event) => {
    event.preventDefault()
    const wanted = email.trim()
    if (!wanted || busy) return
    const before = list?.instructors || []
    setBusy('add')
    setActionError('')
    setAddWarning('')
    const result = await addExperimentInstructor(experimentId, wanted)
    setBusy(null)
    if (result.ok) {
      setEmail('')
      const added = result.instructors.filter((entry) => !before.some((old) => sameAccount(old, entry)))
      const names = [...new Set(added.map((entry) => nameOf(entry) || wanted))].join(', ')
      if (added.length) showToast(`Added ${names}. The experiment is now in their session list.`, 'success', 3000)
      else showToast(`${wanted} can already run this experiment.`, 'info', 3000)
      // Only an instructor account matched: the person may sign in to VizPI with another
      // account instead, one not on the list (an E1 course instructor added this way could not
      // press Start, 2026-10-28). Shown until the next change or edit of the email.
      if (onlyInstructorAccounts(added)) {
        setAddWarning(
          `${wanted} matched only an instructor account. If they sign in to VizPI with a different account, add that account too.`
        )
      }
    }
    settle(result)
  }

  const remove = async (entry) => {
    if (busy) return
    setBusy(accountKey(entry))
    setActionError('')
    setAddWarning('')
    const result = await removeExperimentInstructor(experimentId, entry)
    setBusy(null)
    if (result.ok) showToast(`Removed ${nameOf(entry) || 'the instructor'}.`, 'success', 2500)
    settle(result)
  }

  const instructors = list?.instructors || []
  const canChange = Boolean(list?.canChange)
  // Every added row names its kind of account (Account): a person whose email signs in to both
  // kinds is listed twice under one name. Rows that still look alike - no name, or the same
  // name and kind (two users-table accounts of one email) - also show the account's id.
  const nameKind = (entry) => `${nameOf(entry).toLowerCase()}|${entry.kind}`
  const nameKindCounts = new Map()
  instructors.forEach((entry) => nameKindCounts.set(nameKind(entry), (nameKindCounts.get(nameKind(entry)) || 0) + 1))
  const accountNote = (entry) => (!nameOf(entry) || nameKindCounts.get(nameKind(entry)) > 1 ? `ID ${entry.id}` : '')
  const myName = userName(currentUser) || currentUser?.email || ''

  return (
    <div style={card}>
      <div style={cardHeader}>
        <h3 style={cardTitle}>Instructors who can run this experiment</h3>
      </div>
      <p style={cardSubtitle}>
        Added instructors can start, pause and end the experiment and see its Overview and Presenter, but not change
        its setup. They need to be signed in with an account listed here.
      </p>

      {loadFailure && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            loadFailure.status === 403 || loadFailure.status === 404 ? null : (
              <Button color="inherit" size="small" onClick={load}>
                Try again
              </Button>
            )
          }>
          {loadFailure.message}
        </Alert>
      )}

      {list ? (
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Instructor</TableCell>
              <TableCell sx={headSx}>Account</TableCell>
              <TableCell sx={headSx}>Role</TableCell>
              <TableCell sx={headSx} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={cellSx}>
                {canChange ? (
                  <>
                    {myName || 'You'}
                    {myName && <span style={mutedSx}> (you)</span>}
                  </>
                ) : (
                  <span style={mutedSx}>The instructor who created it</span>
                )}
              </TableCell>
              {/* The server knows the creator by account id alone, of either kind. */}
              <TableCell sx={cellSx} />
              <TableCell sx={cellSx}>Creator</TableCell>
              <TableCell sx={cellSx} align="right" />
            </TableRow>
            {instructors.map((entry) => {
              const key = accountKey(entry)
              const note = accountNote(entry)
              const kind = accountKindLabel(entry.kind)
              return (
                <TableRow key={key}>
                  <TableCell sx={cellSx}>
                    {nameOf(entry) || 'Instructor'}
                    {note && <span style={mutedSx}> ({note})</span>}
                  </TableCell>
                  <TableCell sx={cellSx}>{capitalized(kind)}</TableCell>
                  <TableCell sx={cellSx}>Added instructor</TableCell>
                  <TableCell sx={cellSx} align="right">
                    {canChange && (
                      <button
                        type="button"
                        style={{ ...toolbarButton, display: 'inline-flex', opacity: busy ? 0.6 : 1 }}
                        disabled={Boolean(busy)}
                        aria-label={`Remove ${nameOf(entry) || 'instructor'} (${kind}${note ? `, ${note}` : ''})`}
                        onClick={() => remove(entry)}>
                        {busy === key ? 'Removing…' : 'Remove'}
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      ) : (
        !loadFailure && <p style={{ ...cardSubtitle, margin: 0 }}>Loading…</p>
      )}

      {list && instructors.length === 0 && (
        <p style={{ ...cardSubtitle, margin: '12px 0 0 0' }}>No other instructors yet.</p>
      )}

      {canChange && (
        <form onSubmit={add} noValidate style={{ marginTop: '20px' }}>
          <label htmlFor={EMAIL_INPUT_ID} style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
            Add an instructor by email
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '6px' }}>
            <input
              id={EMAIL_INPUT_ID}
              type="email"
              autoComplete="off"
              placeholder="instructor@school.edu"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                setActionError('')
                setAddWarning('')
              }}
              style={emailInputStyle}
            />
            <button
              type="submit"
              style={{ ...toolbarButton, opacity: !email.trim() || busy ? 0.6 : 1 }}
              disabled={!email.trim() || Boolean(busy)}>
              {busy === 'add' ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      )}
      {list && !canChange && (
        <p style={{ ...cardSubtitle, margin: '12px 0 0 0' }}>
          Only the instructor who created this experiment can change who runs it.
        </p>
      )}

      {addWarning && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {addWarning}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {actionError}
        </Alert>
      )}
    </div>
  )
}

// What was published: the conditions and their settings, how students are assigned, and the
// concept list - in the Monitor's card style - and who can run the experiment besides its
// creator. Each condition is an ordinary session, edited on its own page. `experimentId`
// defaults to the experiment row the page shows (SessionContext).
const ExperimentSetupTab = ({ config, conditions, experimentId }) => {
  const navigate = useNavigate()
  const { session } = useContext(SessionContext) || {}
  const assignment = ASSIGNMENT_MODES.find((mode) => mode.value === config.assignment)
  const id = experimentId ?? session?.id

  return (
    // Grows with its cards (nine conditions run past the window), like the Overview.
    <div style={{ ...dashboardPage, height: 'auto', minHeight: '100%' }}>
      {id != null && <InstructorsCard experimentId={id} />}

      <div style={card}>
        <div style={cardHeader}>
          <h3 style={cardTitle}>Conditions</h3>
        </div>
        <p style={cardSubtitle}>
          Each condition is its own session, copied from the same draft. Changes made in one condition&apos;s session
          apply to that condition only.
        </p>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Condition</TableCell>
              <TableCell sx={headSx}>AI chat</TableCell>
              <TableCell sx={headSx}>Chat policy</TableCell>
              <TableCell sx={headSx}>Offloading panel</TableCell>
              <TableCell sx={headSx} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {conditions.map((condition) => {
              const row = condition.row
              // A condition is a normal session: its chat can be switched (or ended) after
              // publishing. Show what students have now, and the published setting when it differs.
              const chatNow = row ? aiChatEnabled(row) : condition.aiChat
              const chatChanged = row && chatNow !== Boolean(condition.aiChat)
              return (
                <TableRow key={condition.key}>
                  <TableCell sx={cellSx}>{condition.name}</TableCell>
                  <TableCell sx={cellSx}>
                    {chatNow ? 'On' : 'Off'}
                    {chatChanged && (
                      <span style={{ color: '#9ca3af' }}> (published: {condition.aiChat ? 'On' : 'Off'})</span>
                    )}
                  </TableCell>
                  {/* As published; the policy text itself is instructor-only (the condition's
                      session holds it), so this page only records whether there was one. */}
                  <TableCell sx={cellSx}>{chatNow && condition.hasPolicy ? 'Set' : '—'}</TableCell>
                  <TableCell sx={cellSx}>{offloadingLabel(chatNow ? condition.offloading : 'off')}</TableCell>
                  <TableCell sx={cellSx} align="right">
                    {row ? (
                      <button
                        type="button"
                        style={{ ...toolbarButton, display: 'inline-flex' }}
                        onClick={() => navigate(`/session/${row.id}`)}>
                        Open session
                      </button>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>Deleted from the session list</span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <div style={card}>
        <div style={cardHeader}>
          <h3 style={cardTitle}>Assigning students: {assignmentLabel(config.assignment)}</h3>
        </div>
        <p style={{ ...cardSubtitle, margin: 0 }}>{assignment?.description}</p>
      </div>

      {config.concepts?.length > 0 && (
        <div style={card}>
          <div style={cardHeader}>
            <h3 style={cardTitle}>Concepts</h3>
          </div>
          <p style={cardSubtitle}>What the offloading panel tracks.</p>
          <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', lineHeight: 1.7 }}>
            {config.concepts.map((concept) => (
              <li key={concept.id}>
                <strong>{concept.label}</strong>
                {concept.description && <span style={{ color: '#4b5563' }}> — {concept.description}</span>}
                {concept.example && (
                  <div style={{ color: '#6b7280', fontSize: '13px' }}>
                    Example: <code style={{ fontFamily: 'Monaco, Menlo, monospace', fontSize: '12px' }}>{concept.example}</code>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  )
}

export default ExperimentSetupTab
